import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Upload, Send, RefreshCw, Trash2, Edit2, CheckCircle,
    AlertCircle, Clock, Users, Search, X, ChevronDown, Mail
} from 'lucide-react';
import {
    previewCSV, importInvitations, fetchInvitations, fetchInvitationStats,
    updateInvitation, resendInvitation, revokeInvitation, clearPreview,
    COLLEGE_LABELS, COLLEGE_COLORS, STATUS_LABELS, STATUS_COLORS
} from '../../store/slices/invitationSlice';
import CollegeBadge from '../../components/common/CollegeBadge';
import toast from 'react-hot-toast';

// ── Sous-composant : badge statut ───────────────────────────────────────────
const StatusBadge = ({ status }) => (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[status] || 'bg-gray-100 text-gray-600'}`}>
        {STATUS_LABELS[status] || status}
    </span>
);

// ── Sous-composant : modal d'édition ────────────────────────────────────────
const EditModal = ({ invitation, onClose, onSave }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState({
        firstName: invitation.firstName || '',
        lastName: invitation.lastName || '',
        email: invitation.email || '',
        organization: invitation.organization || '',
        college: invitation.college || ''
    });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        const result = await dispatch(updateInvitation({ id: invitation._id, data: form }));
        if (!result.error) {
            toast.success('Invitation mise à jour');
            onSave();
        } else {
            toast.error(result.payload || 'Erreur lors de la mise à jour');
        }
        setSaving(false);
    };

    const Field = ({ label, name, type = 'text' }) => (
        <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">{label}</label>
            <input
                type={type}
                value={form[name]}
                onChange={(e) => setForm(prev => ({ ...prev, [name]: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-ginka focus:outline-none focus:ring-2 focus:ring-primary-red"
            />
        </div>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <h3 className="text-lg font-semibold font-ginka">Modifier l'invitation</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
                </div>
                <div className="px-6 py-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Prénom" name="firstName" />
                        <Field label="Nom" name="lastName" />
                    </div>
                    <Field label="Email" name="email" type="email" />
                    <Field label="Structure" name="organization" />
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 font-ginka">Collège</label>
                        <select
                            value={form.college}
                            onChange={(e) => setForm(prev => ({ ...prev, college: e.target.value }))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-ginka focus:outline-none focus:ring-2 focus:ring-primary-red"
                        >
                            {Object.entries(COLLEGE_LABELS).map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="flex justify-end space-x-3 px-6 py-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-ginka">Annuler</button>
                    <button onClick={handleSave} disabled={saving}
                        className="btn-charte btn-charte-primary px-4 py-2 text-sm disabled:opacity-50 font-ginka">
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Onglet Import ────────────────────────────────────────────────────────────
const ImportTab = ({ onImportDone }) => {
    const dispatch = useDispatch();
    const { preview, isLoading, isImporting } = useSelector((state) => state.invitations);
    const fileRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    // Copie locale des lignes (modifiable) + sélection
    const [localRows, setLocalRows] = useState([]);
    const [selected, setSelected] = useState(new Set());
    const [editingIdx, setEditingIdx] = useState(null);
    const [editForm, setEditForm] = useState({});

    // Quand le preview arrive, initialiser localRows et sélectionner les lignes OK
    useEffect(() => {
        if (!preview) { setLocalRows([]); setSelected(new Set()); setEditingIdx(null); return; }
        setLocalRows(preview.rows.map(r => ({ ...r })));
        setSelected(new Set(
            preview.rows.reduce((acc, r, i) => { if (r.status === 'ok') acc.push(i); return acc; }, [])
        ));
        setEditingIdx(null);
    }, [preview]);

    const handleFile = (file) => {
        if (!file) return;
        if (!file.name.endsWith('.csv')) { toast.error('Seuls les fichiers CSV sont acceptés'); return; }
        dispatch(previewCSV(file));
    };

    const handleDrop = (e) => {
        e.preventDefault(); setDragOver(false);
        handleFile(e.dataTransfer.files[0]);
    };

    // Sélection individuelle
    const toggleRow = (i) => {
        setSelected(prev => {
            const next = new Set(prev);
            next.has(i) ? next.delete(i) : next.add(i);
            return next;
        });
    };

    // Tout sélectionner / désélectionner
    const allSelectable = localRows.reduce((acc, r, i) => {
        if (r.status !== 'skip') acc.push(i); return acc;
    }, []);
    const allChecked = allSelectable.length > 0 && allSelectable.every(i => selected.has(i));
    const toggleAll = () => {
        if (allChecked) {
            setSelected(prev => { const next = new Set(prev); allSelectable.forEach(i => next.delete(i)); return next; });
        } else {
            setSelected(prev => { const next = new Set(prev); allSelectable.forEach(i => next.add(i)); return next; });
        }
    };

    // Ouvrir l'édition d'une ligne
    const startEdit = (i) => {
        setEditingIdx(i);
        setEditForm({ ...localRows[i] });
    };

    // Sauvegarder l'édition localement
    const saveEdit = (i) => {
        setLocalRows(prev => {
            const next = [...prev];
            // Recalculer le statut si erreur corrigée
            const r = { ...editForm };
            const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email || '');
            const hasName = (r.firstName || '').trim() && (r.lastName || '').trim();
            const hasCollege = !!r.college;
            if (r.status === 'error' && hasEmail && hasName && hasCollege) {
                r.status = 'ok';
                r.warnings = [];
                // Auto-sélectionner si correction réussie
                setSelected(prev => { const next = new Set(prev); next.add(i); return next; });
            }
            next[i] = r;
            return next;
        });
        setEditingIdx(null);
    };

    // Envoi des lignes sélectionnées
    const handleConfirmImport = async () => {
        const toSend = [...selected].map(i => localRows[i]).filter(r => r.status === 'ok' || r.status === 'error');
        const valid = toSend.filter(r => r.status === 'ok');
        if (valid.length === 0) { toast.error('Aucune ligne valide sélectionnée'); return; }
        const result = await dispatch(importInvitations(valid));
        if (!result.error) {
            toast.success(`${result.payload.data.sent} invitation(s) envoyée(s) avec succès`);
            dispatch(clearPreview());
            onImportDone();
        } else {
            toast.error(result.payload || 'Erreur lors de l\'import');
        }
    };

    const statusIcon = (status) => ({
        ok: <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />,
        skip: <Clock className="w-4 h-4 text-yellow-500 flex-shrink-0" />,
        error: <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
    }[status] || null);

    const selectedValid = [...selected].filter(i => localRows[i]?.status === 'ok').length;

    return (
        <div className="space-y-6">
            {/* Zone de dépôt */}
            {!preview && (
                <div
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={handleDrop}
                    onClick={() => fileRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary-red bg-red-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                >
                    <Upload className="w-10 h-10 mx-auto mb-3 text-gray-400" />
                    <p className="font-medium text-gray-700 mb-1 font-ginka">Déposez votre fichier CSV ici</p>
                    <p className="text-sm text-gray-400 font-ginka">ou cliquez pour sélectionner</p>
                    <p className="text-xs text-gray-300 mt-2 font-ginka">Format attendu : Prénom ; Nom ; Email ; Structure ; Collège</p>
                    <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e.target.files[0])} />
                </div>
            )}

            {isLoading && (
                <div className="flex items-center justify-center py-8 space-x-3">
                    <div className="w-5 h-5 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
                    <span className="text-gray-600 font-ginka">Analyse du fichier...</span>
                </div>
            )}

            {/* Prévisualisation avec sélection */}
            {preview && !isLoading && (
                <div className="space-y-4">
                    {/* Résumé */}
                    <div className="grid grid-cols-4 gap-3">
                        {[
                            { label: 'Total CSV', value: preview.summary.total, color: 'text-gray-700' },
                            { label: 'Valides', value: preview.summary.toImport, color: 'text-green-700' },
                            { label: 'Ignorées', value: preview.summary.toSkip, color: 'text-yellow-700' },
                            { label: 'Erreurs', value: preview.summary.errors, color: 'text-red-700' }
                        ].map(({ label, value, color }) => (
                            <div key={label} className="bg-white border rounded-lg p-3 text-center">
                                <p className={`text-2xl font-bold ${color}`}>{value}</p>
                                <p className="text-xs text-gray-500 font-ginka">{label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Barre de sélection */}
                    <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                        <div className="flex items-center space-x-3">
                            <input
                                type="checkbox"
                                checked={allChecked}
                                onChange={toggleAll}
                                className="w-4 h-4 rounded border-gray-300 text-primary-red focus:ring-primary-red cursor-pointer"
                            />
                            <span className="text-sm text-blue-800 font-ginka">
                                <strong>{selected.size}</strong> ligne(s) sélectionnée(s) —{' '}
                                <strong className="text-green-700">{selectedValid}</strong> valide(s) à envoyer
                            </span>
                        </div>
                        <div className="flex items-center space-x-2 text-xs text-blue-600 font-ginka">
                            <span>💡 Cliquez sur</span>
                            <Edit2 className="w-3 h-3" />
                            <span>pour corriger une ligne avant envoi</span>
                        </div>
                    </div>

                    {/* Tableau */}
                    <div className="bg-white border rounded-lg overflow-hidden">
                        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0 z-10">
                                    <tr>
                                        <th className="px-3 py-2 w-8">
                                            <input type="checkbox" checked={allChecked} onChange={toggleAll}
                                                className="w-4 h-4 rounded border-gray-300 text-primary-red focus:ring-primary-red cursor-pointer" />
                                        </th>
                                        <th className="px-2 py-2 w-6"></th>
                                        {['Prénom', 'Nom', 'Email', 'Structure', 'Collège', 'Note', ''].map(h => (
                                            <th key={h} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-ginka">{h}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {localRows.map((row, i) => (
                                        <React.Fragment key={i}>
                                            {/* Ligne normale */}
                                            <tr className={`${row.status === 'error' ? 'bg-red-50' : row.status === 'skip' ? 'bg-yellow-50' : ''} ${selected.has(i) && row.status !== 'skip' ? 'bg-green-50' : ''}`}>
                                                <td className="px-3 py-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={selected.has(i)}
                                                        disabled={row.status === 'skip'}
                                                        onChange={() => toggleRow(i)}
                                                        className="w-4 h-4 rounded border-gray-300 text-primary-red focus:ring-primary-red cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                                                    />
                                                </td>
                                                <td className="px-2 py-2">{statusIcon(row.status)}</td>
                                                <td className="px-3 py-2 text-sm font-ginka">{row.firstName}</td>
                                                <td className="px-3 py-2 text-sm font-ginka">{row.lastName}</td>
                                                <td className="px-3 py-2 text-sm text-gray-600 font-ginka">{row.email}</td>
                                                <td className="px-3 py-2 text-sm text-gray-500 font-ginka max-w-[120px] truncate">{row.organization}</td>
                                                <td className="px-3 py-2"><CollegeBadge college={row.college} size="sm" /></td>
                                                <td className="px-3 py-2 text-xs text-gray-400 font-ginka max-w-[160px]">{row.warnings?.join(', ')}</td>
                                                <td className="px-3 py-2">
                                                    {row.status !== 'skip' && (
                                                        <button onClick={() => editingIdx === i ? setEditingIdx(null) : startEdit(i)}
                                                            className={`p-1 rounded transition-colors ${editingIdx === i ? 'text-primary-red' : 'text-gray-300 hover:text-blue-500'}`}
                                                            title="Modifier cette ligne">
                                                            <Edit2 className="w-3.5 h-3.5" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>

                                            {/* Panneau d'édition inline */}
                                            {editingIdx === i && (
                                                <tr className="bg-blue-50 border-t-0">
                                                    <td colSpan={9} className="px-4 py-3">
                                                        <div className="flex flex-wrap gap-3 items-end">
                                                            {[
                                                                { key: 'firstName', label: 'Prénom', w: 'w-28' },
                                                                { key: 'lastName', label: 'Nom', w: 'w-28' },
                                                                { key: 'email', label: 'Email', w: 'w-56' },
                                                                { key: 'organization', label: 'Structure', w: 'w-40' }
                                                            ].map(({ key, label, w }) => (
                                                                <div key={key} className={w}>
                                                                    <label className="block text-xs text-gray-500 mb-1 font-ginka">{label}</label>
                                                                    <input
                                                                        type="text"
                                                                        value={editForm[key] || ''}
                                                                        onChange={(e) => setEditForm(prev => ({ ...prev, [key]: e.target.value }))}
                                                                        className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-red font-ginka"
                                                                    />
                                                                </div>
                                                            ))}
                                                            <div className="w-36">
                                                                <label className="block text-xs text-gray-500 mb-1 font-ginka">Collège</label>
                                                                <select
                                                                    value={editForm.college || ''}
                                                                    onChange={(e) => setEditForm(prev => ({ ...prev, college: e.target.value }))}
                                                                    className="w-full px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-red font-ginka"
                                                                >
                                                                    <option value="">— choisir —</option>
                                                                    {Object.entries(COLLEGE_LABELS).map(([v, l]) => (
                                                                        <option key={v} value={v}>{l}</option>
                                                                    ))}
                                                                </select>
                                                            </div>
                                                            <div className="flex space-x-2 pb-0.5">
                                                                <button onClick={() => saveEdit(i)}
                                                                    className="px-3 py-1 bg-primary-red text-white text-sm rounded hover:bg-red-700 font-ginka">
                                                                    Valider
                                                                </button>
                                                                <button onClick={() => setEditingIdx(null)}
                                                                    className="px-3 py-1 border border-gray-300 text-gray-600 text-sm rounded hover:bg-gray-100 font-ginka">
                                                                    Annuler
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Barre d'actions finale */}
                    <div className="flex justify-between items-center bg-white border rounded-lg px-4 py-3">
                        <button onClick={() => dispatch(clearPreview())}
                            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg font-ginka">
                            Changer de fichier
                        </button>
                        <div className="flex items-center space-x-4">
                            <span className="text-sm text-gray-500 font-ginka">
                                {selectedValid} invitation(s) prête(s) à envoyer
                            </span>
                            <button onClick={handleConfirmImport} disabled={isImporting || selectedValid === 0}
                                className="btn-charte btn-charte-primary flex items-center space-x-2 px-6 py-2 disabled:opacity-50">
                                {isImporting ? (
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                ) : <Send className="w-4 h-4" />}
                                <span className="font-ginka">Envoyer {selectedValid} invitation(s)</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Onglet Liste ─────────────────────────────────────────────────────────────
const ListTab = () => {
    const dispatch = useDispatch();
    const { invitations, isLoading, pagination } = useSelector((state) => state.invitations);
    const [search, setSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterCollege, setFilterCollege] = useState('');
    const [editTarget, setEditTarget] = useState(null);

    const load = () => dispatch(fetchInvitations({ status: filterStatus, college: filterCollege, search }));

    useEffect(() => { load(); }, [filterStatus, filterCollege]);

    const handleSearch = (e) => { e.preventDefault(); load(); };

    const handleResend = async (id, email) => {
        const result = await dispatch(resendInvitation(id));
        if (!result.error) toast.success(`Email renvoyé à ${email}`);
        else toast.error(result.payload || 'Erreur lors du renvoi');
    };

    const handleRevoke = async (id) => {
        if (!window.confirm('Révoquer cette invitation ?')) return;
        const result = await dispatch(revokeInvitation(id));
        if (!result.error) toast.success('Invitation révoquée');
        else toast.error(result.payload || 'Erreur');
    };

    return (
        <div className="space-y-4">
            {editTarget && (
                <EditModal invitation={editTarget} onClose={() => setEditTarget(null)} onSave={() => { setEditTarget(null); load(); }} />
            )}

            {/* Filtres */}
            <div className="flex flex-wrap gap-3 items-center">
                <form onSubmit={handleSearch} className="flex-1 min-w-48 flex items-center border border-gray-300 rounded-lg overflow-hidden">
                    <Search className="w-4 h-4 mx-3 text-gray-400 flex-shrink-0" />
                    <input type="text" placeholder="Rechercher..." value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="flex-1 py-2 text-sm focus:outline-none font-ginka" />
                    {search && <button type="button" onClick={() => { setSearch(''); load(); }} className="px-2 text-gray-400"><X className="w-3 h-3" /></button>}
                </form>

                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-ginka focus:outline-none focus:ring-2 focus:ring-primary-red">
                    <option value="">Tous les statuts</option>
                    {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>

                <select value={filterCollege} onChange={(e) => setFilterCollege(e.target.value)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-ginka focus:outline-none focus:ring-2 focus:ring-primary-red">
                    <option value="">Tous les collèges</option>
                    {Object.entries(COLLEGE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>

                <button onClick={load} className="p-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50">
                    <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                </button>
            </div>

            {/* Tableau */}
            <div className="bg-white border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {['Personne', 'Structure', 'Collège', 'Statut', 'Envoyée le', 'Actions'].map(h => (
                                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-ginka">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {isLoading && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 font-ginka">Chargement...</td></tr>
                            )}
                            {!isLoading && invitations.length === 0 && (
                                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400 font-ginka">Aucune invitation trouvée</td></tr>
                            )}
                            {invitations.map((inv) => (
                                <tr key={inv._id} className="hover:bg-gray-50">
                                    <td className="px-4 py-3">
                                        <p className="text-sm font-medium text-gray-900 font-ginka">{inv.firstName} {inv.lastName}</p>
                                        <p className="text-xs text-gray-400 font-ginka">{inv.email}</p>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-500 font-ginka">{inv.organization || '—'}</td>
                                    <td className="px-4 py-3"><CollegeBadge college={inv.college} size="sm" /></td>
                                    <td className="px-4 py-3"><StatusBadge status={inv.status} /></td>
                                    <td className="px-4 py-3 text-sm text-gray-400 font-ginka">
                                        {new Date(inv.createdAt).toLocaleDateString('fr-FR')}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center space-x-2">
                                            {inv.status !== 'used' && (
                                                <button onClick={() => setEditTarget(inv)}
                                                    className="p-1 text-gray-400 hover:text-blue-600" title="Modifier">
                                                    <Edit2 className="w-4 h-4" />
                                                </button>
                                            )}
                                            {['sent', 'reminded', 'expired'].includes(inv.status) && (
                                                <button onClick={() => handleResend(inv._id, inv.email)}
                                                    className="p-1 text-gray-400 hover:text-green-600" title="Renvoyer">
                                                    <Mail className="w-4 h-4" />
                                                </button>
                                            )}
                                            {inv.status !== 'used' && (
                                                <button onClick={() => handleRevoke(inv._id)}
                                                    className="p-1 text-gray-400 hover:text-red-600" title="Révoquer">
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {pagination.total > 0 && (
                    <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-500 font-ginka">
                        {pagination.total} invitation(s) au total
                    </div>
                )}
            </div>
        </div>
    );
};

// ── Page principale ──────────────────────────────────────────────────────────
const InvitationManagement = () => {
    const dispatch = useDispatch();
    const { stats } = useSelector((state) => state.invitations);
    const [activeTab, setActiveTab] = useState('list');

    useEffect(() => {
        dispatch(fetchInvitationStats());
        dispatch(fetchInvitations());
    }, [dispatch]);

    const statusIcons = { pending: '⏳', sent: '📧', reminded: '🔔', used: '✅', expired: '❌' };

    return (
        <div className="max-w-7xl mx-auto">
            {/* En-tête */}
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 font-ginka h2-charte">Invitations sociétaires</h1>
                <p className="text-gray-500 mt-1 font-ginka p-charte">
                    Importez un CSV, envoyez et gérez les invitations à créer un compte.
                </p>
            </div>

            {/* Stats rapides */}
            {stats && (
                <div className="grid grid-cols-5 gap-3 mb-6">
                    {Object.entries(STATUS_LABELS).map(([status, label]) => {
                        const count = stats.byStatus.find(s => s._id === status)?.count || 0;
                        return (
                            <div key={status} className="bg-white border rounded-lg p-3 text-center">
                                <p className="text-xl font-bold text-gray-800">{count}</p>
                                <p className="text-xs text-gray-500 font-ginka">{statusIcons[status]} {label}</p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Onglets */}
            <div className="border-b border-gray-200 mb-6">
                <nav className="flex space-x-6">
                    {[
                        { id: 'list', label: 'Invitations', icon: Users },
                        { id: 'import', label: 'Importer un CSV', icon: Upload }
                    ].map(({ id, label, icon: Icon }) => (
                        <button key={id} onClick={() => setActiveTab(id)}
                            className={`flex items-center space-x-2 pb-3 border-b-2 text-sm font-medium font-ginka transition-colors ${activeTab === id ? 'border-primary-red text-primary-red' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                            <Icon className="w-4 h-4" />
                            <span>{label}</span>
                        </button>
                    ))}
                </nav>
            </div>

            {activeTab === 'list' && <ListTab />}
            {activeTab === 'import' && (
                <ImportTab onImportDone={() => {
                    setActiveTab('list');
                    dispatch(fetchInvitations());
                    dispatch(fetchInvitationStats());
                }} />
            )}
        </div>
    );
};

export default InvitationManagement;
